import { Box, IconButton, Stack, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { API_BASE_URL } from "src/api/config/axiosInstance";
import { fetchWithAuth } from "src/api/fetchWithAuth";
import { BizmedAiMarkdown } from "src/utils/markdown";
import { stripSseKeepaliveNoise } from "src/utils/sseKeepalive";
import { joinStreamTextFragments } from "src/utils/streamTextJoin";

type AiChatProps = {
  open: boolean;
  onClose: () => void;
};

type ChatRole = "assistant" | "user";

type ChatMessage = {
  id: string;
  role: ChatRole;
  text: string;
  time: string;
  status?: "loading" | "streaming" | "done" | "error";
};

type SpeechRecognitionLike = {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
  maxAlternatives?: number;
  onresult?: (event: any) => void;
  onstart?: () => void;
  onend?: () => void;
  onerror?: () => void;
  start: () => void;
  stop: () => void;
};

const getNowTime = () =>
  new Intl.DateTimeFormat("tr-TR", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date());

const createId = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const shimmerBackground =
  "linear-gradient(90deg, rgba(223,230,240,0.68) 0%, rgba(255,255,255,0.98) 50%, rgba(223,230,240,0.68) 100%)";

const parseSseChunk = (rawEvent: string) => {
  const dataLines = rawEvent
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim());

  if (!dataLines.length) {
    return "";
  }

  const payload = dataLines.join("\n");
  if (!payload || payload === "[DONE]") {
    return "";
  }

  try {
    const parsed = JSON.parse(payload);
    if (typeof parsed === "string") return parsed;
    if (typeof parsed?.text === "string") return parsed.text;
    if (typeof parsed?.data?.text === "string") return parsed.data.text;
    if (typeof parsed?.delta === "string") return parsed.delta;
    if (typeof parsed?.content === "string") return parsed.content;
    if (typeof parsed?.message === "string") return parsed.message;
  } catch {
    return payload;
  }

  return "";
};

const ShimmerLine = ({ width, delay = 0 }: { width: string; delay?: number }) => (
  <Box
    sx={{
      width,
      height: 11,
      borderRadius: 999,
      background: shimmerBackground,
      backgroundSize: "200% 100%",
      animation: `ai-chat-shimmer 1.5s linear ${delay}s infinite`,
    }}
  />
);

function LoadingBubble() {
  return (
    <Box
      sx={{
        maxWidth: "86%",
        p: "16px 16px 14px 16px",
        borderRadius: "18px 18px 6px 18px",
        bgcolor: "rgba(255, 255, 255, 0.88)",
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        border: "1px solid rgba(226, 232, 240, 0.95)",
      }}
    >
      <Stack spacing={1.1}>
        <ShimmerLine width="116px" />
        <ShimmerLine width="228px" delay={0.08} />
        <ShimmerLine width="204px" delay={0.16} />
        <ShimmerLine width="164px" delay={0.24} />
      </Stack>
    </Box>
  );
}

export default function AiChat({ open, onClose }: AiChatProps) {
  const theme = useTheme();
  const isLandscapeCompact = useMediaQuery("(max-height: 500px)");
  const isLandscapeNarrow = useMediaQuery("(orientation: landscape) and (max-width: 1024px)");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const shouldKeepRecordingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      abortRef.current = null;
      shouldKeepRecordingRef.current = false;
      try {
        recognitionRef.current?.stop?.();
      } catch {
      }
      setIsRecording(false);
      setIsSending(false);
      setInputValue("");
      setMessages([]);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [messages, open]);

  const updateAssistantMessage = (id: string, updater: (prev: ChatMessage) => ChatMessage) => {
    setMessages((prev) => prev.map((item) => (item.id === id ? updater(item) : item)));
  };

  const handleSend = async () => {
    const message = inputValue.trim();
    if (!message || isSending) return;

    const userMessage: ChatMessage = {
      id: createId(),
      role: "user",
      text: message,
      time: getNowTime(),
      status: "done",
    };

    const assistantId = createId();
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: "assistant",
      text: "",
      time: getNowTime(),
      status: "loading",
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);
    setInputValue("");
    setIsSending(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetchWithAuth(`${API_BASE_URL}/external/api/AiAssistant/Chat`, {
        method: "POST",
        headers: {
          Accept: "text/event-stream",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ message }),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Chat request failed: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let receivedAnyChunk = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        buffer = stripSseKeepaliveNoise(buffer);
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";

        for (const eventChunk of events) {
          const chunk = stripSseKeepaliveNoise(eventChunk).trim();
          if (!chunk) continue;
          const textChunk = chunk.includes("data:") ? parseSseChunk(chunk) : chunk;

          if (!textChunk) continue;
          receivedAnyChunk = true;

          updateAssistantMessage(assistantId, (prev) => ({
            ...prev,
            text: joinStreamTextFragments(prev.text, textChunk),
            status: "streaming",
          }));
        }
      }

      const tail = stripSseKeepaliveNoise(buffer).trim();
      if (tail) {
        const tailText = tail.includes("data:") ? parseSseChunk(tail) : tail;
        if (tailText) {
          receivedAnyChunk = true;
          updateAssistantMessage(assistantId, (prev) => ({
            ...prev,
            text: joinStreamTextFragments(prev.text, tailText),
            status: "streaming",
          }));
        }
      }

      updateAssistantMessage(assistantId, (prev) => ({
        ...prev,
        text: prev.text.trim() || (receivedAnyChunk ? prev.text : "Yanit alinamadi."),
        status: "done",
      }));
    } catch (error: any) {
      if (error?.name === "AbortError") {
        updateAssistantMessage(assistantId, (prev) => ({
          ...prev,
          status: prev.text ? "done" : "error",
          text: prev.text || "Islem iptal edildi.",
        }));
      } else {
        updateAssistantMessage(assistantId, () => ({
          id: assistantId,
          role: "assistant",
          text: "Yanit alinamadi. Lütfen tekrar deneyin.",
          time: getNowTime(),
          status: "error",
        }));
      }
    } finally {
      abortRef.current = null;
      setIsSending(false);
    }
  };

  const stopRecognition = () => {
    shouldKeepRecordingRef.current = false;
    setIsRecording(false);
    try {
      recognitionRef.current?.stop?.();
    } catch {
    }
  };

  const startRecognition = () => {
    const SpeechRecognitionCtor =
      (globalThis as any).SpeechRecognition ||
      (globalThis as any).webkitSpeechRecognition;

    if (!SpeechRecognitionCtor) {
      return;
    }

    try {
      if (!recognitionRef.current) {
        const recognition = new SpeechRecognitionCtor();
        recognition.lang = "tr-TR";
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onresult = (event: any) => {
          const results = event?.results;
          if (!results) return;

          let finalText = "";
          for (let i = event.resultIndex ?? 0; i < results.length; i += 1) {
            const res = results[i];
            const transcript = res?.[0]?.transcript?.toString?.() ?? "";
            if (res?.isFinal && transcript) {
              finalText += `${finalText ? " " : ""}${transcript.trim()}`;
            }
          }

          if (!finalText) return;
          setInputValue((prev) => (prev ? `${prev.trim()} ${finalText}` : finalText));
        };

        recognition.onstart = () => {
          setIsRecording(true);
        };

        recognition.onend = () => {
          if (shouldKeepRecordingRef.current) {
            try {
              recognition.start();
              return;
            } catch {
            }
          }
          setIsRecording(false);
        };

        recognition.onerror = () => {
          shouldKeepRecordingRef.current = false;
          setIsRecording(false);
        };

        recognitionRef.current = recognition as unknown as SpeechRecognitionLike;
      }

      shouldKeepRecordingRef.current = true;
      setIsRecording(true);
      recognitionRef.current.start();
    } catch {
    }
  };

  const handleMicClick = () => {
    if (isSending) return;
    if (isRecording) {
      stopRecognition();
      return;
    }
    startRecognition();
  };

  if (!open) return null;

  return (
    <Box
      sx={{
        position: "fixed",
        right: isLandscapeNarrow ? 12 : { xs: 0, md: 28 },
        left: isLandscapeNarrow ? 12 : { xs: 0, md: "auto" },
        bottom: isLandscapeNarrow ? 12 : { xs: 0, md: 128 },
        top: isLandscapeNarrow ? 12 : "auto",
        width: isLandscapeNarrow
          ? "auto"
          : { xs: "100vw", sm: 390 },
        maxWidth: isLandscapeNarrow
          ? "none"
          : { xs: "100vw", md: "calc(100vw - 16px)" },
        height: isLandscapeNarrow
          ? "auto"
          : { xs: "min(84vh, 760px)", md: 620 },
        maxHeight: isLandscapeNarrow
          ? "calc(100vh - 24px)"
          : isLandscapeCompact
            ? "calc(100vh - 16px)"
            : "calc(100vh - 24px)",
        bgcolor: "rgba(255, 255, 255, 0.70)",
        borderRadius: isLandscapeNarrow ? "20px" : { xs: "24px 24px 0 0", md: "24px" },
        border: "1px solid rgba(255, 255, 255, 0.30)",
        backdropFilter: "blur(6px)",
        boxShadow: { xs: "0px -18px 40px rgba(15, 23, 42, 0.24)", md: "0px 25px 50px -12px rgba(0, 0, 0, 0.25)" },
        overflow: "hidden",
        zIndex: 1301,
        display: "flex",
        flexDirection: "column",
        "@keyframes ai-chat-shimmer": {
          "0%": { backgroundPosition: "200% 0" },
          "100%": { backgroundPosition: "-200% 0" },
        },
      }}
    >
      <Box
        sx={{
          p: isLandscapeNarrow
            ? "12px 14px"
            : { xs: "18px 20px 16px 20px", md: "16px 20px" },
          borderBottom: "1px solid rgba(229, 231, 235, 0.50)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          position: "relative",
        }}
      >
        <Box
          sx={{
            display: { xs: "block", md: "none" },
            ...(isLandscapeNarrow && {
              display: "none",
            }),
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            width: 44,
            height: 5,
            borderRadius: 999,
            backgroundColor: "#CBD5E1",
          }}
        />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            component="img"
            src="/assets/icons/nurse/ai-nurse-icon.png"
            alt="ai-nurse-icon"
            sx={{
              width: 24,
              height: 24,
              flexShrink: 0,
              objectFit: "contain",
            }}
          />
          <Typography
            sx={{
              color: "#0F172A",
              fontSize: isLandscapeNarrow ? 13 : 14,
              fontFamily: "Poppins",
              fontWeight: 600,
              lineHeight: "20px",
            }}
          >
            Yapay Zeka Asistanım
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#6B7280" }}>
          <Box component="svg" viewBox="0 0 24 24" sx={{ width: 18, height: 18, display: "block" }}>
            <Box component="line" x1="18" y1="6" x2="6" y2="18" sx={{ stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }} />
            <Box component="line" x1="6" y1="6" x2="18" y2="18" sx={{ stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round" }} />
          </Box>
        </IconButton>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          p: isLandscapeNarrow ? 1.5 : 2.5,
          display: "flex",
          flexDirection: "column",
          gap: isLandscapeNarrow ? 1.25 : 2,
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        {messages.length === 0 && (
          <Box
            sx={{
              flex: 1,
              minHeight: isLandscapeNarrow ? 96 : 180,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              px: 3,
              color: "#94A3B8",
              fontSize: 14,
              lineHeight: "22px",
              fontFamily: "Poppins",
            }}
          >
            Hemsire asistanina soru yazabilir veya mikrofon ile sesli olarak iletebilirsiniz.
          </Box>
        )}

        {messages.map((m) => {
          const isUser = m.role === "user";
          return (
            <Box
              key={m.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: isUser ? "flex-end" : "flex-start",
              }}
            >
              {m.status === "loading" ? (
                <LoadingBubble />
              ) : (
                <Box
                  sx={{
                    maxWidth: "86%",
                    p: isLandscapeNarrow ? "10px 12px" : "14px 16px",
                    borderRadius: isUser ? "18px 18px 18px 6px" : "18px 18px 6px 18px",
                    bgcolor: isUser ? "#3B82F6" : "rgba(255, 255, 255, 0.80)",
                    color: isUser ? "#fff" : m.status === "error" ? "#B42318" : "#374151",
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    border: m.status === "error" ? "1px solid rgba(254, 205, 211, 1)" : "1px solid transparent",
                    fontSize: isLandscapeNarrow ? 12 : isUser ? 12 : 14,
                    fontFamily: "Poppins",
                    fontWeight: isUser ? 300 : 400,
                    lineHeight: isLandscapeNarrow ? "18px" : isUser ? "15.6px" : "22.75px",
                    whiteSpace: isUser ? "pre-wrap" : "normal",
                  }}
                >
                  {isUser ? (
                    m.text
                  ) : m.status === "error" ? (
                    m.text
                  ) : (
                    <BizmedAiMarkdown markdown={m.text} variant="chatbubble" normalize />
                  )}
                </Box>
              )}
              <Box
                sx={{
                  mt: 0.5,
                  px: 0.5,
                  color: "#9CA3AF",
                  fontSize: 10,
                  fontFamily: "Inter",
                  lineHeight: "15px",
                }}
              >
                {m.time}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ p: isLandscapeNarrow ? "0 14px 14px 14px" : "0 20px 20px 20px" }}>
        <Box
          sx={{
            px: 1.25,
            py: isLandscapeNarrow ? 0.75 : 1,
            bgcolor: "#fff",
            borderRadius: "20px",
            border: "1px solid #E5E7EB",
            boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Box
            component="input"
            value={inputValue}
            disabled={isSending}
            onChange={(e: any) => setInputValue(e.target.value)}
            onKeyDown={(e: any) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Yaz ...."
            sx={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: isLandscapeNarrow ? 13 : 14,
              fontFamily: "Poppins",
              color: "#111827",
              bgcolor: "transparent",
              opacity: isSending ? 0.6 : 1,
              "&::placeholder": { color: "#9CA3AF" },
            }}
          />
          <Box
            component="button"
            type="button"
            onClick={handleMicClick}
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              border: "none",
              p: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: isRecording ? "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)" : "transparent",
              boxShadow: isRecording ? "0px 8px 20px rgba(99, 102, 241, 0.34)" : "none",
              cursor: isSending ? "not-allowed" : "pointer",
              opacity: isSending ? 0.45 : 1,
              transition: "all 0.2s ease",
            }}
          >
            <Box
              component="img"
              src="/assets/icons/ai-chat/microphone.svg"
              alt="microphone"
              sx={{
                width: 18,
                height: 18,
                flexShrink: 0,
                opacity: 0.9,
                filter: isRecording
                  ? "brightness(0) invert(1)"
                  : "brightness(0) saturate(100%) invert(30%) sepia(82%) saturate(2200%) hue-rotate(248deg) brightness(97%) contrast(94%)",
              }}
            />
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleSend}
            disabled={isSending || !inputValue.trim()}
            sx={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              border: "none",
              p: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "transparent",
              cursor: isSending || !inputValue.trim() ? "not-allowed" : "pointer",
              opacity: isSending || !inputValue.trim() ? 0.45 : 1,
            }}
          >
            <Box
              component="img"
              src="/assets/icons/ai-chat/send.svg"
              alt="send"
              sx={{
                width: 18,
                height: 18,
                flexShrink: 0,
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

